<template>
  <div>
    <div style="margin-bottom: 20px;">
      <app-button
        :label="t('Adicionar Serviço')"
        icon="pi pi-plus"
        size="small"
        data-testid="so-services-add-btn"
        @click="handleNew"
      />
    </div>

    <!-- SO Services Table -->
    <s-o-services-table
      ref="soServicesTableRef"
      @edit="handleEdit"
      @delete="handleDelete">
    </s-o-services-table>

    <!-- SOServicesModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import SOServicesTable from './SOServicesTable.vue';
// SOServicesModal is centralized in ModalManager.vue
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface Props {
  translations?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  translations: () => ({})
});

interface DataTableInstance {
  loadData: () => void;
}

// Get i18n composable
const { t } = useI18n();

// Reactive data
const soServicesTableRef = ref<DataTableInstance | null>(null);

// Methods
const loadData = async (): Promise<void> => {
  log('SOServicesPage', 'loadData', 'Reloading data');
  if (soServicesTableRef.value) {
    await soServicesTableRef.value.loadData();
  }
};

const handleNew = (): void => {
  log('SOServicesPage', 'handleNew', 'Opening modal for new service via eventBus');
  eventBus.emit('modal.so-services.open', { action: 'create' });
};

const handleEdit = (serviceId: Id): void => {
  log('SOServicesPage', 'handleEdit', 'Editing service ID via eventBus', serviceId);
  eventBus.emit('modal.so-services.open', { id: serviceId, action: 'edit' });
};

const handleDelete = (serviceId: Id, serviceData: Record<string, unknown> | null = null): void => {
  log('SOServicesPage', 'handleDelete', 'Deleting service ID via eventBus', serviceId);
  eventBus.emit('modal.so-services.open', { id: serviceId, data: serviceData, action: 'delete' });
};

// Backward-compatible eventBus listeners (typed to match EventCallback shape)
const onBusNew = (): void => handleNew();
const onBusEdit = (data?: Id): void => {
  if (data !== undefined) handleEdit(data);
};
const onBusDelete = (data?: Id): void => {
  if (data !== undefined) handleDelete(data);
};

// Lifecycle
onMounted(() => {
  log('SOServicesPage', 'mounted', 'Component mounted');

  // Set up eventBus listeners for backward compatibility
  eventBus.on('so-services.new', onBusNew);
  eventBus.on<Id>('so-services.edit', onBusEdit);
  eventBus.on<Id>('so-services.delete', onBusDelete);
});

onBeforeUnmount(() => {
  log('SOServicesPage', 'beforeUnmount', 'Cleaning up event listeners');

  // Clean up eventBus listeners
  eventBus.off('so-services.new', onBusNew);
  eventBus.off<Id>('so-services.edit', onBusEdit);
  eventBus.off<Id>('so-services.delete', onBusDelete);
});

// Expose methods for parent components
defineExpose({
  loadData,
  handleNew,
  handleEdit,
  handleDelete
});
</script>

<style scoped>
</style>
